import { Component, Input, forwardRef, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';

import type { SocialSeriesMetricsEnum } from '../../../../Social/Intelligence/enums/social-series-metrics.enum';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { AbstractFormFieldComponent } from '../../../PageBuilder/components/FormField/abstract-form-field.component';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { ChartMetrics, ChartMetricsEnum, MentionSeriesMetricsOnly, SocialSeriesMetricsOnly } from '../../enums/chart-metrics.enum';
import { ChartAnalysisMetricDropdownFieldModel } from '../../models/chart-analysis-metric-dropdown-field.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { mentionSeriesTypes, SeriesTypeEnum, socialSeriesTypes } from '../../enums/series-type.enum';

@Component({
    selector: 'agpr-multiple-metrics-picker',
    templateUrl: './multiple-metrics-picker.component.html',
    styleUrls: ['./multiple-metrics-picker.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => MultipleMetricsPickerComponent)
        }
    ],
    standalone: false
})
export class MultipleMetricsPickerComponent extends AbstractFormFieldComponent implements OnInit, OnChanges, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;

    @Input() declare field: ChartAnalysisMetricDropdownFieldModel;
    @Input() declare value: ChartMetricsEnum[];

    selectedMetrics: ChartMetricsEnum[] = [];
    selectedMetricsOptions: {
        options: DropdownOptionModel<ChartMetricsEnum>[];
        selection: DropdownOptionModel<ChartMetricsEnum>;
    }[] = [
        {
            options: [],
            selection: null
        }
    ];
    maxNumberOfMetrics: number;

    private subscriptions: Subscription[] = [];
    /** We don't want to affect the field.options directly, so we use a copy */
    private allOptions: DropdownOptionModel<ChartMetricsEnum>[] = [];
    private currentSourceType: SeriesTypeEnum;
    private hasInit: boolean = false;

    constructor(
        protected localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.maxNumberOfMetrics = this.field?.multiMetricSettings.maxNumberOfMetrics;
        this.subscriptions.push(
            this.currentUserStateService.getPermissions().subscribe(permissions => {
                if (!permissions) {
                    return;
                }
                this.allOptions = this.field.options.slice();
                this.selectedMetrics = [];
                this.filterOptions(
                    !permissions.includes(userPermissionsEnum.socialIntelligence),
                    !permissions.includes(userPermissionsEnum.advancedMonitoring)
                );
                // Update old audienceReach values to Impressions
                this.value = this.value?.map(metric => {
                    if (metric === ChartMetricsEnum.audienceReach) {
                        return ChartMetricsEnum.IMPRESSIONS;
                    }
                    return metric;
                });
                this.setDefaultSelection();
                this.setSelections();
            })
        );
        this.hasInit = true;
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }

    onDropdownChanged(value: ChartMetricsEnum, index: number) {
        this.selectedMetrics[index] = value;
        this.onSelectedMetricsChange();
    }

    ngOnChanges(changes: SimpleChanges) {
        // We need to filter options if the series source type changes to match metrics for each source type
        const seriesType = (this.block.displayElement as ChartDisplayElementModel)._series[0]?.type;
        if (!this.hasInit || seriesType === this.currentSourceType) {
            return;
        }

        this.allOptions = this.field.options.slice();
        this.currentSourceType = seriesType;

        const anyMonitoring = mentionSeriesTypes.includes(seriesType);
        // Keep SocialSeriesMetricsOnly options available, even when the source type is not social
        this.filterOptions(false, !anyMonitoring);
        // Convert all old values to new ones using new available field options
        this.selectedMetrics = this.selectedMetrics.map(value => {
            const matchingOption = this.allOptions.find(opt => opt.value === value);
            return matchingOption?.value ?? this.getNextValidOptionValue();
        });
        this.onSelectedMetricsChange();
    }

    addMetric() {
        this.selectedMetrics.push(this.getNextValidOptionValue());
        this.selectedMetrics = this.selectedMetrics.slice();
        this.onSelectedMetricsChange();
    }

    deleteMetric(index: number) {
        this.selectedMetrics.splice(index, 1);
        this.selectedMetrics = this.selectedMetrics.slice();
        this.onSelectedMetricsChange();
    }

    private setDefaultSelection() {
        if (this.allOptions && this.allOptions.length > 0) {
            if (!this.value) {
                this.value = [this.allOptions[0].value];
            }
            this.selectedMetrics.push(...this.value.slice(0, this.field?.multiMetricSettings.maxNumberOfMetrics));
            this.changes.emit(this.selectedMetrics);
        }
    }

    private onSelectedMetricsChange() {
        this.setSelections();
        this.changes.emit(this.selectedMetrics);
    }

    private filterOptions(excludeSocial: boolean, excludeMonitoring: boolean) {
        if (excludeSocial) {
            this.allOptions = this.allOptions.filter(option => !SocialSeriesMetricsOnly.includes(option.value as SocialSeriesMetricsEnum));
        }
        if (excludeMonitoring) {
            this.allOptions = this.allOptions.filter(option => !MentionSeriesMetricsOnly.includes(option.value as ChartMetrics));
        }
        if (this.allOptions.length < this.selectedMetrics.length){
            // if the selected metrics is more than our options, trim the selected metrics array
            this.selectedMetrics = this.selectedMetrics.slice(0, this.allOptions.length);
        }
        this.maxNumberOfMetrics = Math.min(this.allOptions.length, this.field?.multiMetricSettings.maxNumberOfMetrics);
    }

    private setSelections() {
        this.selectedMetricsOptions = this.selectedMetrics
            .map(selectedMetric => {
                return {
                    // Filter out selected options in other dropdowns
                    options: this.allOptions.filter(opt => opt.value === selectedMetric || !this.selectedMetrics.includes(opt.value)),
                    selection: this.allOptions.find(opt => opt.value === selectedMetric)
                };
            })
            // Sort options based on selectedMetrics order
            .sort((a, b) => this.selectedMetrics?.indexOf(a.selection?.value) - this.selectedMetrics?.indexOf(b.selection?.value));
    }

    private getNextValidOptionValue(): ChartMetricsEnum {
        return this.allOptions.find(opt => !this.selectedMetrics.includes(opt.value)).value;
    }
}

